/**
 * RemoteAvatars — owns the Avatar instances for every player except us.
 *
 * Subscribes to ZoneClient snapshot updates, lerps positions, and maps
 * ids to procedural Avatar groups in the scene.
 */

import * as THREE from 'three';
import { Avatar } from '@/player/Avatar';
import type { ZoneClient } from '@/net/ZoneClient';
import { dampAngle } from '@/core/MathUtils';

interface Slot {
  avatar: Avatar;
  lastName: string;
}

const INTERP_MS = 130; // smooth out 8 Hz updates

export class RemoteAvatars {
  private slots = new Map<number, Slot>();

  constructor(private scene: THREE.Scene, private net: ZoneClient) {
    net.on((e) => {
      if (e.type === 'left') this.removeSlot(e.id);
      if (e.type === 'disconnected') {
        for (const id of Array.from(this.slots.keys())) this.removeSlot(id);
      }
    });
  }

  private removeSlot(id: number): void {
    const slot = this.slots.get(id);
    if (!slot) return;
    this.scene.remove(slot.avatar.group);
    slot.avatar.dispose();
    this.slots.delete(id);
  }

  update(dt: number): void {
    const now = performance.now();
    for (const [id, rp] of this.net.remotePlayers) {
      let slot = this.slots.get(id);
      if (!slot) {
        const avatar = Avatar.build({
          hatColor: rp.hatColor,
          name: rp.name
        });
        avatar.group.position.set(rp.x, rp.y, rp.z);
        this.scene.add(avatar.group);
        slot = { avatar, lastName: rp.name };
        this.slots.set(id, slot);
      }
      if (rp.name !== slot.lastName) {
        slot.avatar.setName(rp.name);
        slot.lastName = rp.name;
      }
      // Interpolate between prev and target snapshots
      const span = Math.max(1, rp.target.t - rp.prev.t);
      const t = Math.min(1, (now - rp.target.t + INTERP_MS) / span);
      const x = rp.prev.x + (rp.target.x - rp.prev.x) * t;
      const y = rp.prev.y + (rp.target.y - rp.prev.y) * t;
      const z = rp.prev.z + (rp.target.z - rp.prev.z) * t;
      slot.avatar.group.position.set(x, y, z);
      slot.avatar.group.rotation.y = dampAngle(slot.avatar.group.rotation.y, rp.target.yaw, 14, dt);
      const speed = Math.hypot(rp.target.x - rp.prev.x, rp.target.z - rp.prev.z) / (span / 1000);
      slot.avatar.setAnim(rp.anim);
      slot.avatar.pose(dt, speed);
    }
  }
}
